<template>
  <div class="w-full">
    <div
      class="bg-white/95 backdrop-blur-sm rounded-lg sm:rounded-xl shadow-lg border border-(--p-surface-200) p-2.5 sm:p-4 mb-2 sm:mb-4 sm:sticky top-[95px] z-50"
    >
      <div
        class="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 sm:gap-4"
      >
        <div class="flex items-center gap-2 sm:gap-4 w-full lg:w-auto">
          <div class="flex items-center gap-2 sm:gap-3">
            <div
              class="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-(--p-primary-500) to-(--p-primary-600) rounded-lg sm:rounded-xl flex items-center justify-center shadow-md"
            >
              <i class="pi pi-th-large text-white text-lg sm:text-xl"></i>
            </div>
            <div>
              <div
                class="text-lg sm:text-xl lg:text-2xl font-bold text-(--p-anthracite-500) leading-tight"
              >
                Action Board
              </div>
              <div class="text-xs sm:text-sm text-(--p-anthracite-500) font-medium">Kanban View</div>
            </div>
          </div>
        </div>

        <div
          class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full lg:w-auto"
        >
          <div v-if="!isMobile" class="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            <div
              class="inline-flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 bg-(--p-primary-500) text-white rounded-md sm:rounded-lg text-[10px] sm:text-xs font-medium border border-blue-200"
            >
              <i class="pi pi-list text-[10px] sm:text-xs"></i>
              <span class="font-bold">{{ totalVisibleActions }}</span>
              <span class="hidden min-[1740px]:inline">{{
                totalVisibleActions === 1 ? 'Action' : 'Actions'
              }}</span>
            </div>
            <div
              v-if="overdueCount > 0"
              class="inline-flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 bg-(--p-tangerine-500) text-white rounded-md sm:rounded-lg text-[10px] sm:text-xs font-medium border border-red-200"
            >
              <i class="pi pi-exclamation-triangle text-[10px] sm:text-xs"></i>
              <span class="font-bold">{{ overdueCount }}</span>
              <span class="hidden sm:inline">Overdue</span>
            </div>
          </div>

          <div class="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto">
            <Button
              v-if="
                user &&
                (user as { access?: number }).access !== undefined &&
                (user as { access?: number }).access !== null &&
                (user as { access?: number }).access! >= 5
              "
              icon="pi pi-plus"
              label="New Action"
              severity="primary"
              size="small"
              @click="onCreateAction"
              class="flex-1 sm:flex-none min-h-[44px] sm:min-h-0"
            />
            <ActionFilter
              v-model="filterState"
              :plans="plans"
              @display-settings-changed="onDisplaySettingsChanged"
              class="flex-1 sm:flex-none"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-3 lg:gap-4">
      <div
        v-for="col in columns"
        :key="col.key"
        class="rounded-lg border-2 transition-all duration-200 flex flex-col"
        :class="[
          hoverCol === col.key
            ? 'border-(--p-primary-400) shadow-lg bg-(--p-primary-50)'
            : 'border-(--p-surface-300) bg-(--p-surface-0)',
        ]"
      >
        <div
          class="px-2.5 sm:px-3 lg:px-4 py-2 sm:py-3 flex items-center justify-between bg-gradient-to-br from-(--p-primary-500) to-(--p-primary-600) sm:sticky top-[180px] z-40 rounded-t-lg"
        >
          <div class="flex items-center gap-1 sm:gap-2 min-w-0 flex-1">
            <i
              :class="getColumnIcon(col.key)"
              class="text-xs sm:text-sm text-white flex-shrink-0"
            ></i>
            <div class="font-semibold text-white text-[11px] sm:text-sm lg:text-base truncate">
              {{ col.label }}
            </div>
          </div>
          <div class="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            <div
              class="text-[10px] sm:text-xs font-medium text-white bg-white/20 px-1.5 sm:px-2 py-0.5 rounded-full"
            >
              {{ counts[col.key] || 0 }}
            </div>
            <Button
              v-if="
                user &&
                (user as { access?: number }).access !== undefined &&
                (user as { access?: number }).access !== null &&
                (user as { access?: number }).access! >= 5
              "
              icon="pi pi-plus"
              severity="secondary"
              text
              rounded
              size="small"
              class="!text-white hover:bg-white/20 hover:!text-black"
              @click="onCreateActionWithState(col.key)"
              v-tooltip.top="`Add new ${col.label.toLowerCase()} action`"
            />
          </div>
        </div>
        <div
          class="flex flex-col flex-1 min-h-[200px] sm:min-h-[300px] p-2 sm:p-3 gap-2 overflow-y-auto custom-scrollbar rounded-b-lg"
          :class="{ 'bg-(--p-primary-50)': hoverCol === col.key }"
          @dragenter.prevent="onColEnter(col.key)"
          @dragleave.prevent="onColLeave(col.key)"
          @dragover.prevent
          @drop="onDrop($event, col.key)"
        >
          <transition-group name="card-list">
            <div
              v-for="a in filteredGrouped[col.key] || []"
              :key="a.id"
              class="transition-all duration-200 ease-out"
              :class="{ 'opacity-50 scale-[0.98]': dnd.dragging?.id === a.id }"
            >
              <button
                class="w-full text-left bg-white border border-(--p-surface-300) hover:border-(--p-primary-300) hover:shadow-md transition-all duration-200 rounded-lg px-2.5 sm:px-3 py-2 sm:py-2.5 group min-h-[60px] sm:min-h-0"
                :style="{
                  borderLeftWidth: '4px',
                  borderLeftColor: a.colorHex || '#60a5fa',
                  backgroundColor: a.colorHex ? a.colorHex + '08' : 'white',
                }"
                draggable="true"
                @dragstart="onDragStart($event, a)"
                @dragend="onDragEnd"
                @click="onActionClick(a)"
                v-tooltip.top="'Click to edit, drag to move'"
              >
                <div class="flex items-start justify-between gap-2 mb-2">
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2">
                      <div
                        class="font-medium text-sm text-(--p-anthracite-500) line-clamp-2 group-hover:text-(--p-primary-600) transition-colors"
                      >
                        {{ a.title }}
                      </div>
                      <i
                        v-if="canSeePrivate && a.isPrivate"
                        class="pi pi-lock text-(--p-anthracite-500) text-[10px]"
                        v-tooltip.top="'Private action'"
                      ></i>
                    </div>
                  </div>
                  <div v-if="displaySettings.showColorIndicator" class="flex-shrink-0">
                    <i
                      class="pi pi-circle-fill"
                      :style="{ color: a.colorHex || '#60a5fa', fontSize: '0.5rem' }"
                    ></i>
                  </div>
                </div>

                <div class="flex flex-wrap gap-2 items-center">
                  <div
                    v-if="displaySettings.showDueDate"
                    class="inline-flex items-center gap-1 text-xs px-2 py-1 rounded"
                    :class="
                      isOverdue(a)
                        ? 'bg-red-100 text-red-700 font-medium'
                        : 'bg-(--p-surface-100) text-(--p-anthracite-500)'
                    "
                  >
                    <i
                      :class="isOverdue(a) ? 'pi pi-exclamation-triangle' : 'pi pi-calendar'"
                      class="text-[10px]"
                    ></i>
                    <span>{{ formatShort(a.dueDate) }}</span>
                  </div>

                  <div
                    v-if="displaySettings.showKpiArea && a.kpiArea"
                    class="inline-flex items-center gap-1 text-xs px-2 py-1 rounded"
                    :class="getKpiAreaColorClass()"
                  >
                    <i class="pi pi-chart-line text-[10px]"></i>
                    <span class="truncate max-w-[120px]">{{ a.kpiArea }}</span>
                  </div>

                  <div
                    v-if="
                      displaySettings.showRetailer &&
                      (getActionRetailer(a, plans) || getActionGroups(a, plans).length > 0)
                    "
                    class="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-(--p-primary-500) text-white"
                  >
                    <i
                      :class="
                        getActionGroups(a, plans).length > 0
                          ? 'pi pi-users text-[10px] text-white'
                          : 'pi pi-building text-[10px] text-white'
                      "
                    ></i>
                    <span class="truncate max-w-[100px]">{{
                      getActionRetailer(a, plans) ||
                      (getActionGroups(a, plans).length > 0
                        ? getActionGroups(a, plans)[0]?.label
                        : '')
                    }}</span>
                  </div>
                </div>

                <div
                  v-if="isOverdue(a) && displaySettings.showOverdueWarning"
                  class="mt-2 flex items-center gap-1 text-xs text-red-600 font-semibold"
                >
                  <i class="pi pi-exclamation-circle text-[10px]"></i>
                  <span>OVERDUE</span>
                </div>
              </button>
            </div>
          </transition-group>

          <div
            v-if="filteredGrouped[col.key].length === 0"
            class="flex-1 flex flex-col items-center justify-center text-center py-8 text-(--p-anthracite-500)"
          >
            <i class="pi pi-inbox !text-3xl mb-2 opacity-50"></i>
            <div class="text-sm">No actions in {{ col.label.toLowerCase() }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, onMounted, onUnmounted } from 'vue'
import type { Action } from '@/config/types/Action'
import type { Plan } from '@/config/types/Plan'
import useActionFilters from '@/composables/ActionFilters'
import useActions from '@/composables/Actions'
import useDisplaySettings from '@/composables/useDisplaySettings'
import useAuth from '@/composables/Auth'

const props = defineProps<{
  plans: Plan[]
}>()
const emit = defineEmits<{
  (e: 'action:click', value: Action): void
  (e: 'action:move', payload: { action: Action; newState: 'pending' | 'active' | 'complete' }): void
  (e: 'action:create'): void
  (e: 'action:create:state', state: 'pending' | 'active' | 'complete'): void
}>()

const columns: Array<{ key: 'pending' | 'active' | 'complete'; label: string }> = [
  { key: 'pending', label: 'Open' },
  { key: 'active', label: 'Active' },
  { key: 'complete', label: 'Complete' },
]

const filterState = ref({
  searchQuery: '',
  selectedStates: ['pending', 'active', 'complete'],
  selectedKpiAreas: [] as string[],
  selectedDueDateFilters: [] as string[],
  selectedRetailers: [] as (string | number)[],
})

const { settings: displaySettings } = useDisplaySettings()
const { isOverdue, formatShort, filterActions } = useActionFilters()
const { getActionRetailer, getActionGroups, getStateIcon, getKpiAreaColorClass } = useActions()
const { user } = useAuth()
const canSeePrivate = computed(() => {
  const access = (user.value as { access?: number })?.access
  return access !== undefined && access !== null && access >= 5
})

const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024)
const isMobile = computed(() => windowWidth.value < 700)

const handleResize = () => {
  if (typeof window !== 'undefined') {
    windowWidth.value = window.innerWidth
  }
}

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', handleResize)
  }
})

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', handleResize)
  }
})

const flat = computed<Action[]>(() => {
  const list: Action[] = []
  for (const p of props.plans || []) {
    if (Array.isArray(p.actions)) list.push(...p.actions)
  }
  return list
})

const filteredActions = computed(() => {
  return filterActions(flat.value, filterState.value, props.plans)
})

const filteredGrouped = computed(() => {
  const map: Record<'pending' | 'active' | 'complete', Action[]> = {
    pending: [],
    active: [],
    complete: [],
  }
  for (const a of filteredActions.value || []) map[a.state].push(a)
  for (const k of Object.keys(map) as ['pending', 'active', 'complete'])
    map[k].sort((x, y) => String(x.title).localeCompare(String(y.title)))
  return map
})

const counts = computed(() => ({
  pending: filteredGrouped.value.pending.length,
  active: filteredGrouped.value.active.length,
  complete: filteredGrouped.value.complete.length,
}))

const totalVisibleActions = computed(() => {
  return filteredActions.value.length
})

const overdueCount = computed(() => {
  return filteredActions.value.filter((a) => isOverdue(a)).length
})

// Use centralized state icon
const getColumnIcon = (key: 'pending' | 'active' | 'complete') => {
  return getStateIcon(key)
}

const dnd = reactive<{
  dragging: Action | null
  hoverCol: 'pending' | 'active' | 'complete' | null
}>({
  dragging: null,
  hoverCol: null,
})

const onDragStart = (ev: DragEvent, a: Action) => {
  dnd.dragging = a
  try {
    ev.dataTransfer?.setData('text/plain', String(a.id))
    ev.dataTransfer!.effectAllowed = 'move'
  } catch {}
}

const onDragEnd = () => {
  dnd.dragging = null
  dnd.hoverCol = null
}

const onColEnter = (col: 'pending' | 'active' | 'complete') => {
  dnd.hoverCol = col
}

const onColLeave = (col: 'pending' | 'active' | 'complete') => {
  if (dnd.hoverCol === col) dnd.hoverCol = null
}

const onDrop = (ev: DragEvent, col: 'pending' | 'active' | 'complete') => {
  ev.preventDefault()
  const a = dnd.dragging
  dnd.hoverCol = null
  if (!a) return
  if (a.state === col) return
  emit('action:move', { action: a, newState: col })
}

const onDisplaySettingsChanged = (settings: typeof displaySettings.value) => {
  displaySettings.value = { ...settings }
}

const onActionClick = (a: Action) => {
  emit('action:click', a)
}

const onCreateAction = () => {
  emit('action:create')
}

const onCreateActionWithState = (state: 'pending' | 'active' | 'complete') => {
  emit('action:create:state', state)
}

const hoverCol = computed(() => dnd.hoverCol)
</script>

<style scoped>
.card-list-move {
  transition: transform 0.3s ease;
}

.card-list-enter-active,
.card-list-leave-active {
  transition: all 0.3s ease;
}

.card-list-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.card-list-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

[draggable='true'] {
  cursor: grab;
}

[draggable='true']:active {
  cursor: grabbing;
}

.a11y-reduced-motion .card-list-move,
.a11y-reduced-motion .card-list-enter-active,
.a11y-reduced-motion .card-list-leave-active {
  transition: none !important;
}
</style>
